import moment from 'moment';

// The frequencies a rule can be saved with, in picker order. Hourly is retired:
// Daily plus Times of Day expresses every sub-day cadence, the API converts
// stored hourly rules on read, and the Frequency column never carries one.
export var FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'];

// The dashboard's half of Global Constraint 3: these constants and helpers
// must always agree with the API's InspectionRecurrenceEngine
// (Legacy/SL8/Services/Areas/Inspect/InspectionRecurrenceEngine.cs). See
// CadenceEditor.test.js's "interval ceiling agreement with the API" test,
// which pins the values below against that file.

// MaxIntervalFor: all four ceilings express the same 50-year horizon in the
// frequency's own unit. Daily counts its days directly (+12 leap-year
// slack); weekly is that same daily ceiling rounded UP to whole weeks
// ((18262 + 6) / 7, floored); monthly and yearly count their own units
// directly.
// The monthly and yearly day-of-month cap. A yearly rule fires on its start date's calendar day
// and a monthly rule on its chosen day, and the API skips any year or month that lacks that day
// rather than clamping (InspectionRecurrenceEngine's Candidates). Rulings 2026-09-14 (yearly)
// and 2026-09-15 (monthly): neither picker offers anything above the 28th, so nothing is ever
// silently skipped. Mirrors InspectionRecurrenceEngine.MaxDayOfMonth.
export var MAX_DAY_OF_MONTH = 28;

// The API models "the last day of the month" as -1. It never skips a month.
export var LAST_DAY_OF_MONTH = -1;

// The options a day-of-month select offers: 1..28, Last Day when the rule can use it (monthly),
// then any stored day above the cap. The API does NOT normalise a stored day on a current-shape
// document (only the legacy-shape migration maps 29-31 to Last Day), so that trailing option is
// what keeps such a rule visible: without it a controlled select would silently show the first
// option while the stored value stayed out of range. Any edit replaces it.
export function dayOfMonthOptions(selected, includeLastDay) {
  var options = [];
  for (var d = 1; d <= MAX_DAY_OF_MONTH; d++) options.push(d);
  if (includeLastDay) options.push(LAST_DAY_OF_MONTH);
  if (selected !== null && selected !== undefined && selected > MAX_DAY_OF_MONTH) options.push(selected);
  return options;
}

// "1st", "15th": moment's locale ordinal, shared by the picker and the summary text.
export function ordinal(n) {
  return moment.localeData().ordinal(n);
}

export var MAX_INTERVAL = {
  daily: 18262,
  weekly: 2609,
  monthly: 600,
  yearly: 50,
};

// DaysPerUnit: days in one interval of each frequency, for seeding and for
// the grace ceiling. Approximate by design -- a grace window is a
// tolerance, not an occurrence.
var DAYS_PER_UNIT = {
  daily: 1,
  weekly: 7,
  monthly: 30,
  yearly: 365,
};

// SeedGraceInterval / MaxGraceIntervalFor (API) mirror these: the suggestion is a quarter of the
// gap between consecutive occurrences, capped at two weeks; the ceiling is the whole gap. The gap,
// not the nominal interval, is what matters: monthly on the 1st and 15th has a 15-day gap, not 30.
// 2026-09-15 ruling: the ceiling was half the gap (2026-09-09) so a completion could never credit
// the next occurrence while the previous one was still open; Jeremy chose to allow that - a window
// as long as the gap means any completion after the last due date counts toward the next.
export var GRACE_DEFAULTS = {
  SEED_FRACTION: 0.25,
  SEED_CAP_DAYS: 14,
  MAX_FRACTION: 1,
};

// Mirrors ClampedRecurrenceInterval: floored to 1, ceiling-clamped to
// MaxIntervalFor, so a corrupted or pre-validation interval can't reach the
// multiplication below.
export function clampedInterval(frequency, interval) {
  var max = MAX_INTERVAL[frequency] || 1;
  var n = interval === null || interval === undefined ? 1 : interval;
  if (isNaN(n)) n = 1;
  return Math.min(Math.max(1, n), max);
}

// Mirrors IsDayCoarse (hours.length === 0, now that Hourly is retired and no
// longer needs its own carve-out): an empty/absent/malformed hours list means
// the rule expresses days, not exact instants. A rule naming times of day
// measures its due dates -- and its grace -- in instants, not days. The API's
// validator rejects a grace unit that disagrees with this, so
// seedGraceFor/maxGraceFor always return the two paired: never a value in one
// unit tagged with the other. Exported so any other picker anchored to the
// rule's own coarseness (e.g. FirstDueDateEditor's noon-vs-day-start choice)
// mirrors this single predicate rather than inventing a parallel one.
export function isDayCoarse(hoursOfDay) {
  return !Array.isArray(hoursOfDay) || hoursOfDay.length === 0;
}

// Named times only mean anything on a Daily rule (2026-09-03 spec, decision 1). Every coarseness
// read in the dashboard goes through this, so a daily rule's times, retained across a switch to
// Weekly, can never flip the early-window unit or the first-due anchoring of a rule that cannot
// use them. The wire boundary (inspectionPatchHelper) sends exactly this array as well.
export function effectiveHoursOfDay(frequency, hoursOfDay) {
  return frequency === 'daily' && Array.isArray(hoursOfDay) ? hoursOfDay : [];
}

// Takes hours that have already been gated by effectiveHoursOfDay (which
// seedGraceFor and maxGraceFor do for themselves).
// Exported so the patch helper (the wire boundary) can derive the grace
// unit fresh from the rule's own coarseness rather than trusting a stored
// graceUnit that could be stale relative to a since-changed hoursOfDay --
// the API validator rejects a mismatched pair, so the patch must never send
// one.
export function graceUnitFor(hours) {
  return isDayCoarse(hours) ? 'days' : 'hours';
}

// Distinct entries, at least one -- the API's Normalized* helpers dedupe, so a duplicated slot
// must not shrink the gap here and widen the ceiling there.
function slotCount(list) {
  var seen = [];
  (Array.isArray(list) ? list : []).forEach(function (v) {
    if (seen.indexOf(v) === -1) seen.push(v);
  });
  return Math.max(1, seen.length);
}

// The AVERAGE gap between consecutive occurrences in the rule's own grace unit, as `total` units
// spread over `slots` occurrences: weekly rules recur once per selected weekday, monthly once per
// selected day of month, and a rule naming times of day once per named hour on each of those
// days. Slot arrays only count for the frequency that reads them (the wire boundary sends []
// otherwise), so a weekday list retained across a switch to Monthly cannot shrink the window.
// Returned unreduced so the two callers below divide exactly the way the API's integer
// arithmetic does.
// ponytail: average, not minimum. Clustered slots (weekly Mon+Tue, daily at 08:00 and 09:00) have
// a shorter real gap than this reports; an exact cyclic minimum needs per-frequency wraparound
// code in three mirrors for rules nobody sensibly configures. Upgrade if a customer does.
function occurrenceGap(rule) {
  var r = rule || {};
  var n = clampedInterval(r.frequency, r.interval);
  var hours = effectiveHoursOfDay(r.frequency, r.hoursOfDay);
  var unit = graceUnitFor(hours);
  var daySlots = 1;
  if (r.frequency === 'weekly') daySlots = slotCount(r.daysOfWeek);
  if (r.frequency === 'monthly') daySlots = slotCount(r.daysOfMonth);
  var total = n * (DAYS_PER_UNIT[r.frequency] || 1);
  var slots = daySlots;
  if (unit === 'hours') {
    total = total * 24;
    slots = daySlots * slotCount(hours);
  }
  return { total: total, slots: slots, unit: unit };
}

// Mirrors MaxGraceIntervalFor: the gap, floored, never negative.
export function maxGraceFor(rule) {
  var gap = occurrenceGap(rule);
  return { value: Math.floor((gap.total * GRACE_DEFAULTS.MAX_FRACTION) / gap.slots), unit: gap.unit };
}

// The validator's completion-window ceiling check, run client-side at Save so
// the error lands on the field before the Confirm dialog opens instead of
// after a round trip (the API still enforces it). Reads the same interval the
// patch helper sends: whatever number is in graceInterval, custom or not.
// 'schedule' is the form's mode value (the API's 'scheduled' is mapped both
// ways by the resource and patch helpers). Returns the error text, or null
// when the window fits or there is nothing to check.
export function graceCeilingError(recurrence, t) {
  if (!recurrence || recurrence.mode !== 'schedule') return null;
  var interval = recurrence.graceInterval;
  if (interval === null || interval === undefined || interval === '') return null;
  var max = maxGraceFor(recurrence);
  if (interval <= max.value) return null;
  return t('inspect:graceCeilingError', {
    count: max.value,
    unit: t(unitKey(max.unit, max.value)),
  });
}

// Mirrors SeedGraceInterval: a quarter of the gap rounded to NEAREST (10% of a week would floor to
// 0, the whole reason grace is stored as a duration), capped at two weeks (336 hours) so a yearly
// rule does not suggest a five-week window, and never above the ceiling.
export function seedGraceFor(rule) {
  var gap = occurrenceGap(rule);
  var cap = gap.unit === 'hours' ? GRACE_DEFAULTS.SEED_CAP_DAYS * 24 : GRACE_DEFAULTS.SEED_CAP_DAYS;
  var quarter = Math.round((gap.total * GRACE_DEFAULTS.SEED_FRACTION) / gap.slots);
  return { value: Math.min(quarter, cap, maxGraceFor(rule).value), unit: gap.unit };
}

// True when the rule carries everything a due date can be projected from: an interval of at
// least one, plus the slot array its own frequency needs (daily and yearly need neither). A
// dry run of anything less is a wasted round trip -- the interval field alone emits an
// incomplete rule on every backspace of a retyped number.
export function isCompleteRule(recurrence) {
  var r = recurrence || {};
  if (r.interval === null || r.interval === undefined || r.interval < 1) return false;
  if (r.frequency === 'weekly') return !!(r.daysOfWeek && r.daysOfWeek.length);
  if (r.frequency === 'monthly') return !!(r.daysOfMonth && r.daysOfMonth.length);
  return true;
}

// The inspect:unit_* key for a duration: singular at exactly one, plural
// otherwise. Shared so the cadence suffix ("every 2 weeks") and the early
// submission suffix ("up to 3 days before") can never drift apart.
export function unitKey(unit, count) {
  return 'inspect:unit_' + (count === 1 ? unit.replace(/s$/, '') : unit);
}

// The number inputs on this section all read the same way: an empty field is
// null (absent, distinct from a deliberate zero) and anything else is a
// non-negative integer. Callers apply their own ceiling afterwards.
export function parseNonNegativeInt(raw) {
  if (raw === '') return null;
  var value = parseInt(raw, 10);
  if (isNaN(value) || value < 0) return 0;
  return value;
}
